import { AnalyticsEventIdentifyProperties } from 'src/app/core/services/analytics/models/analytics-event-identify-properties';

/**
 * Will attempt to mask single value field on the provide identify event with values from the provided map.
 *
 * - If 'event' is undefined, not a object, or doesnt contain 'field', it'll return the event right away.
 * - If the event[field] value doesnt have a mapping in the provided map remove the field and return the event.
 *
 * @param field - The field we want to obfuscate.
 * @param event - The event containing said field.
 * @param obfuscatedValueMap - The map of possible values event[field] should be and what to map them too.
 * @returns The provided object, with the field mapped if they exist.
 */
export function obfuscateIdentifyEventSingleValue<T extends AnalyticsEventIdentifyProperties>(
  field: string,
  event: T,
  obfuscatedValueMap: Map<string, string>,
): T {
  // Nothing was passed in, or event its not a object, or the object doesnt contain the field, return it right away.
  if (!event || typeof event !== 'object' || !event[field]) return event;

  const obfuscatedValue = obfuscatedValueMap.get(event[field]);
  if (!obfuscatedValue) {
    // Delete the funding type if of a unknown value and return the updated event to parent. Log as error so we can correct.
    console.warn(`Unknown value for field: ${field} when attempting to obfuscate.`);
    delete event[field];
    return event;
  }

  // Return a new version of the event with the field replaced
  return {
    ...event,
    [field]: obfuscatedValue,
  };
}

/**
 * Will attempt to mask array of strings on a field on the provide identify event with values from the provided map. If
 * a value in the array doesnt exist in the map, it will remove that value. If the array is empty at the end, it will
 * remove the field.
 *
 * - If 'event' is undefined, not a object, or doesnt contain 'field', it'll return the event right away.
 * - If the event[field] isnt an array, delete the field to prevent leaking data to segment and return the event.
 * - If the event[fields] value doesnt have a mapping in the provided map remove the field and return the event.
 *
 * @param field - The field we want to obfuscate.
 * @param event - The event containing said field.
 * @param obfuscatedValueMap - The map of possible values event[field] should contain and what to map them too.
 * @returns The provided object, with the field mapped if they exist.
 */
export function obfuscateIdentifyEventArray<T extends AnalyticsEventIdentifyProperties>(
  field: string,
  event: T,
  obfuscatedValueMap: Map<string, string>,
): T {
  // Nothing was passed in, or event its not a object, or the object doesnt contain the field, return it right away.
  if (!event || typeof event !== 'object' || !event[field]) return event;

  // If the event field is not a array, remove it and return (play it safe)
  // This handles if 'event' doesnt have the field or if field is something like a string
  if (!Array.isArray(event[field])) {
    console.warn(
      "'obfuscateArray' has been misused, event is either undefined, not a object, or the field doesnt exist within it.",
    );
    delete event[field];
    return event;
  }

  // Maps the array of values to be masked to their new value, removing unknown values along the way
  const obfuscatedArray = event[field].reduce((obfuscatedValues, value) => {
    const obfuscatedValue = obfuscatedValueMap.get(value);
    if (!obfuscatedValue) {
      // Throw away value as we cannot mask, and move to the next value.
      console.warn(`Unknown value for field: ${field} in array when attempting to obfuscate.`);
      return obfuscatedValues;
    }
    // Appends the masked valuee to the list
    return [...obfuscatedValues, obfuscatedValue];
  }, []);

  if (obfuscatedArray.length === 0) {
    delete event[field];
    return event;
  }

  // Return a new version of the event with the array in field replaced
  return {
    ...event,
    [field]: obfuscatedArray,
  };
}
